import { type JSX } from 'react';

import { DATA_TABLE_SUB_ROW_INDENT_GRID as SUB_ROW_INDENT_GRID } from '../../../constants.js';
import { useDataTable } from '../../../contexts/DataTableContext.js';
import { getSubRowCellWidth } from './SubRowExpanders.js';

/**
 * Default accessor for the subRows
 * @internal
 */
export function defaultSubRowsAccessor<TData>(originalRow: TData): TData[] {
  return (originalRow as TData & { subRows: TData[] })?.subRows;
}

/**
 * Corrects the subrow header width in situations where content width is not sufficient.
 * This is the case when rows are virtualized or paginated.
 * @internal
 */
export function SubRowsHeaderWidthCorrection(
  props: Readonly<{ subRowOpenDepth: number }>,
): JSX.Element {
  const { subRowOpenDepth } = props;
  const table = useDataTable();
  const hasMultiplePages =
    table.options.enablePagination === true && table.getPageCount() > 1;

  let width: string | undefined;
  if (hasMultiplePages || table.getHasVirtualizedRows()) {
    const indentWidth = SUB_ROW_INDENT_GRID * subRowOpenDepth;
    width = getSubRowCellWidth(
      table.atoms.visualVariantConfig?.get().rowDensity,
      indentWidth,
    );
  }

  return (
    <div
      style={{
        // Expand to fill available space in the cell.
        // This is especially important for right-aligned columns
        // to push the subrow expander all the way to the left.
        flex: 1,
        // Ensure correct width based on the current sub-row max depth,
        // if table has virtualized or paginated rows.
        width,
      }}
    />
  );
}
